import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY, fetchStoreSiteContent } from '../supabaseClient';
import { buildStoreSiteContentMap, normalizeBrandingNavItems } from '../storeSiteContent';

const STORAGE_BRIDGE = (tenantId, restaurantName, displayMode, siteNavPages) => `
  <script>
    (function () {
      var tenantId = ${JSON.stringify(tenantId)};
      var restaurantName = ${JSON.stringify(restaurantName || '')};
      var displayMode = ${JSON.stringify(displayMode || 'kiosk')};
      var exempt = { ss_supabase_v1: true, ss_currency_v1: true };
      window.__SS_URL__ = ${JSON.stringify(SUPABASE_URL)};
      window.__SS_ANON_KEY__ = ${JSON.stringify(SUPABASE_ANON_KEY)};
      window.__SS_ACCESS_TOKEN__ = '';
      window.__SS_DISPLAY_MODE__ = displayMode;
      window.__SS_PUBLIC_CUSTOMER_VIEW__ = true;

      var realStore = window.localStorage;
      var _get = Storage.prototype.getItem.bind(realStore);
      var _set = Storage.prototype.setItem.bind(realStore);
      var _remove = Storage.prototype.removeItem.bind(realStore);
      var _key = Storage.prototype.key.bind(realStore);

      function mapKey(key) {
        if (typeof key !== 'string') return key;
        if (!key.startsWith('ss_')) return key;
        if (exempt[key]) return key;
        return 'tenant:' + tenantId + ':' + key;
      }

      var proxied = {
        getItem: function(key) { return _get(mapKey(key)); },
        setItem: function(key, val) { _set(mapKey(key), val); },
        removeItem: function(key) { _remove(mapKey(key)); },
        key: function(i) { return _key(i); },
        clear: function() {},
        get length() { return realStore.length; }
      };

      try {
        Object.defineProperty(window, 'localStorage', {
          get: function() { return proxied; },
          configurable: true,
        });
      } catch (e) {
        realStore.getItem = function(key) { return _get(mapKey(key)); };
        realStore.setItem = function(key, val) { _set(mapKey(key), val); };
        realStore.removeItem = function(key) { _remove(mapKey(key)); };
      }

      window.__SS_TENANT_ID__ = tenantId;
      window.sessionStorage.setItem('ss_active_tenant', tenantId);

      if (restaurantName) {
        var storeKey = 'tenant:' + tenantId + ':ss_store_v1';
        var existing = _get(storeKey);
        if (!existing) {
          _set(storeKey, JSON.stringify({ storeName: restaurantName }));
        }
      }

      var siteNavPages = ${JSON.stringify(siteNavPages || null)};
      if (Array.isArray(siteNavPages) && siteNavPages.length) {
        localStorage.setItem('ss_store_pages_v1', JSON.stringify(siteNavPages));
      }
    })();
  </script>
`;

const buildSiteNavPages = (rows) => {
  const contentMap = buildStoreSiteContentMap(rows);
  const navItems = normalizeBrandingNavItems(contentMap.branding);
  // 'menu' page (Order Now) is always first — the ordering page itself
  const pages = [{ page_key: 'menu', is_visible: true, is_archived: false, is_landing: false, sort_order: 0 }];
  navItems
    .filter(item => !item.hidden && item.to !== '/order')
    .forEach((item, i) => {
      const pageKey = String(item.to || '').replace(/^\//, '') || item.label.toLowerCase();
      pages.push({ page_key: pageKey, label: item.label, is_visible: true, is_archived: false, is_landing: false, sort_order: i + 1 });
    });
  return pages;
};

const PublicOrderingPage = ({ mode = 'kiosk', forcedSlug = '' }) => {
  const { slug: routeSlug } = useParams();
  const slug = forcedSlug || routeSlug;
  const [restaurant, setRestaurant] = useState(null);
  const [siteNavPages, setSiteNavPages] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [srcDoc, setSrcDoc] = useState('');

  useEffect(() => {
    let active = true;
    supabase
      .from('restaurants')
      .select('id, name')
      .eq('slug', slug)
      .single()
      .then(({ data }) => {
        if (!active) return;
        if (!data) { setNotFound(true); return; }
        setRestaurant(data);
        return fetchStoreSiteContent(data.id);
      })
      .then((rows) => {
        if (!active || !rows) return;
        setSiteNavPages(buildSiteNavPages(rows));
      })
      .catch(() => {});

    return () => { active = false; };
  }, [slug]);

  const bridge = useMemo(
    () => (restaurant ? STORAGE_BRIDGE(restaurant.id, restaurant.name, mode, siteNavPages) : ''),
    [mode, restaurant, siteNavPages]
  );

  useEffect(() => {
    if (!restaurant) return;
    let active = true;

    fetch('/display.html')
      .then((response) => response.text())
      .then((html) => {
        const injected = html.includes('<head>')
          ? html.replace('<head>', `<head><base href="/">${bridge}`)
          : `${bridge}${html}`;
        if (active) setSrcDoc(injected);
      });

    return () => {
      active = false;
    };
  }, [bridge, restaurant]);

  if (notFound) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#334155' }}>
        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🏪</div>
        <h1 style={{ margin: '0 0 8px', fontSize: '1.5rem', fontWeight: 800 }}>Store not found</h1>
        <p style={{ margin: 0, color: '#64748b' }}>The store <strong>{slug}</strong> doesn't exist.</p>
      </div>
    );
  }

  if (!srcDoc) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#64748b' }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', background: '#fff' }}>
      <iframe
        key={`${restaurant?.id}-${mode}`}
        title={`${restaurant?.name || slug} ${mode}`}
        srcDoc={srcDoc}
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </div>
  );
};

export default PublicOrderingPage;